import { StripeElementsOptions } from '@stripe/stripe-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Configuration, PaymentsApi } from '@/api';
import useCreatePaymentMethodSetup from '@/api/mutations/useCreatePaymentMethodSetup';
import useUpdateSubscriptionMutation from '@/api/mutations/useUpdateSubscriptionMutation';
import useGetBillingInfoQuery from '@/api/queries/useGetBillingInfoQuery';
import Button from '@/components/Button';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { isBillingAddressComplete } from '@/models/Billing';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import PaymentMethodSetupForm from '@/modules/billing/components/PaymentMethodSetupForm';
import { BillingDetailsEditPath, BillingHomePath } from '@/modules/billing/paths';
import {
  DefaultPaymentMethodPendingError,
  waitForDefaultPaymentMethod,
} from '@/modules/billing/services/waitForDefaultPaymentMethod';
import { stripePromise } from '@/modules/billing/stripe';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import { Elements } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const UpgradePlanPaymentSetupUnavailable = () => {
  return (
    <>
      <SettingsHeader title="Upgrade Plan" icon={<ArrowUpCircleIcon className="w-6 h-6" />} />
      <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
        Unable to start payment method setup. Please try again.
      </div>
    </>
  );
};

const getUpgradeErrorMessage = (error: unknown) =>
  error instanceof DefaultPaymentMethodPendingError
    ? 'Your payment method was added, but it is still being processed. Please retry the plan upgrade in a moment.'
    : 'Your payment method was added, but the plan upgrade could not be completed. Please try again.';

const UpgradePlanPaymentSetupContent = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [setupConfirmed, setSetupConfirmed] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string>();
  const updateSubscription = useUpdateSubscriptionMutation();
  const { createSetup, setup, isLoading, error } = useCreatePaymentMethodSetup();
  const {
    billingInfo,
    isLoading: isLoadingBillingInfo,
    error: billingInfoError,
  } = useGetBillingInfoQuery();
  const paymentsApi = useMemo(() => new PaymentsApi(new Configuration(auth)), [auth]);
  const hasCompleteBillingAddress = isBillingAddressComplete(billingInfo?.billingDetails?.address);

  useEffect(() => {
    if (!isLoadingBillingInfo && hasCompleteBillingAddress) {
      void createSetup();
    }
  }, [createSetup, hasCompleteBillingAddress, isLoadingBillingInfo]);

  useEffect(() => {
    if (isLoadingBillingInfo || billingInfoError || hasCompleteBillingAddress) {
      return;
    }

    navigate(BillingDetailsEditPath, { replace: true });
  }, [billingInfoError, hasCompleteBillingAddress, isLoadingBillingInfo, navigate]);

  const elementsOptions = useMemo<StripeElementsOptions | undefined>(() => {
    if (!setup?.clientSecret) return undefined;
    return { clientSecret: setup.clientSecret };
  }, [setup?.clientSecret]);

  const upgradeToPaidPlan = useCallback(async () => {
    await waitForDefaultPaymentMethod(() => paymentsApi.getDefaultPaymentMethod());

    await updateSubscription.mutateAsync({
      plan: 'paid',
      billingCycle: 'monthly',
    });

    toast.custom((t) => (
      <CustomToast id={t.id} message={'Plan upgraded successfully!'} visible={t.visible} />
    ));

    navigate(BillingHomePath, {
      state: { upgraded: true },
    });
  }, [navigate, paymentsApi, updateSubscription]);

  const handleSuccess = async () => {
    setSetupConfirmed(true);
    setUpgradeError(undefined);

    try {
      await upgradeToPaidPlan();
    } catch (caughtError) {
      console.error('Error upgrading plan:', caughtError);
      setUpgradeError(getUpgradeErrorMessage(caughtError));
    }
  };

  const retryUpgrade = async () => {
    setUpgradeError(undefined);

    try {
      await upgradeToPaidPlan();
    } catch (caughtError) {
      console.error('Error retrying plan upgrade:', caughtError);
      setUpgradeError(getUpgradeErrorMessage(caughtError));
    }
  };

  if (isLoadingBillingInfo || (!billingInfoError && !hasCompleteBillingAddress) || isLoading) {
    return (
      <>
        <SettingsHeader title="Upgrade Plan" icon={<ArrowUpCircleIcon className="w-6 h-6" />} />
        <div className="flex justify-center py-20">
          <SpinnerIcon />
        </div>
      </>
    );
  }

  if (setupConfirmed) {
    return (
      <>
        <SettingsHeader title="Upgrade Plan" icon={<ArrowUpCircleIcon className="w-6 h-6" />} />
        {upgradeError ? (
          <div className="rounded-md bg-amber-50 p-4 text-sm text-amber-800">
            <p>{upgradeError}</p>
            <Button
              type="button"
              variant="primary"
              className="mt-4"
              loading={updateSubscription.isPending}
              onClick={() => void retryUpgrade()}
            >
              Retry upgrade
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 py-20">
            <SpinnerIcon />
            <p className="text-sm text-gray-600">Waiting for your payment method to be ready...</p>
          </div>
        )}
      </>
    );
  }

  if (
    billingInfoError ||
    error ||
    updateSubscription.error ||
    !elementsOptions ||
    !setup?.clientSecret
  ) {
    return <UpgradePlanPaymentSetupUnavailable />;
  }

  return (
    <>
      <SettingsHeader title="Upgrade Plan" icon={<ArrowUpCircleIcon className="w-6 h-6" />} />

      <div>
        <p className="mb-4 text-sm text-gray-600">
          Add a payment method to upgrade your plan. Your card details are handled securely by
          Stripe.
        </p>

        <Elements stripe={stripePromise} options={elementsOptions}>
          <PaymentMethodSetupForm clientSecret={setup.clientSecret} onSuccess={handleSuccess} />
        </Elements>
      </div>
    </>
  );
};

const UpgradePlanPaymentSetup = () => {
  if (!stripePromise) return <UpgradePlanPaymentSetupUnavailable />;
  return <UpgradePlanPaymentSetupContent />;
};

export default UpgradePlanPaymentSetup;
